export const SERVICE_DETAIL_INCLUDES = [
    'additionalDetails',
    'graphs',
    'billing',
] as const

export const SERVICE_TYPES = {
    CLOUD_CONNECT: 'Cloud Connect',
    INTERNET: 'Internet',
    BROADBAND: 'Broadband',
    DNS: 'DNS',
    CLOUD_DIRECT: 'Cloud Direct',
    DATA_CENTRE_CONNECT: 'Data Centre Connect',
    DC_PORT: 'DC Port',
    VEEAM_BACKUP: 'Veeam Backup',
} as const

export const SERVICE_KINDS = {
    CONNECTIVITY: 'connectivity',
    BROADBAND: 'broadband',
    VEEAM: 'veeam',
    DNS: 'dns',
    GENERIC: 'generic',
} as const

export type TServiceDetailInclude = (typeof SERVICE_DETAIL_INCLUDES)[number]
export type TServiceType = (typeof SERVICE_TYPES)[keyof typeof SERVICE_TYPES]
export type TServiceKind = (typeof SERVICE_KINDS)[keyof typeof SERVICE_KINDS]

export const getServiceDetailIncludes = (
    includeBilling = true
): TServiceDetailInclude[] =>
    SERVICE_DETAIL_INCLUDES.filter(
        (include) => includeBilling || include !== 'billing'
    )

export const isServiceType = (value: string): value is TServiceType =>
    Object.values(SERVICE_TYPES).includes(value as TServiceType)

export const SERVICE_KIND_BY_TYPE: Record<TServiceType, TServiceKind> = {
    [SERVICE_TYPES.CLOUD_CONNECT]: SERVICE_KINDS.CONNECTIVITY,
    [SERVICE_TYPES.INTERNET]: SERVICE_KINDS.CONNECTIVITY,
    [SERVICE_TYPES.BROADBAND]: SERVICE_KINDS.BROADBAND,
    [SERVICE_TYPES.DNS]: SERVICE_KINDS.DNS,
    [SERVICE_TYPES.CLOUD_DIRECT]: SERVICE_KINDS.CONNECTIVITY,
    [SERVICE_TYPES.DATA_CENTRE_CONNECT]: SERVICE_KINDS.CONNECTIVITY,
    [SERVICE_TYPES.DC_PORT]: SERVICE_KINDS.CONNECTIVITY,
    [SERVICE_TYPES.VEEAM_BACKUP]: SERVICE_KINDS.VEEAM,
}

export const USAGE_SUPPORTED_SERVICE_TYPES = new Set<TServiceType>([
    SERVICE_TYPES.BROADBAND,
])

export type TServiceUsageMetric = {
    id: string
    label: string
    value: string | number
    unit?: string
}

export type TServiceUsageData = {
    metrics: TServiceUsageMetric[]
    days: Array<{
        day: number
        download: number
        upload: number
    }>
    raw: unknown
    empty: boolean
}

export type TServiceGraphMeta = {
    id: string
    label: string
    unit?: string
    type?: string
}

export type TServiceGraphSeries = {
    label: string
    data: number[][]
}

export type TServiceGraphHistory = {
    dataIn: TServiceGraphSeries
    dataOut: TServiceGraphSeries
    rows: Array<{
        timestamp: number
        inbound?: number
        outbound?: number
    }>
    empty: boolean
}

export type TServiceBilling = {
    install?: number
    rental?: number
    billedTo?: string
    [key: string]: unknown
} | null

export type TServiceAdditionalDetails = {
    aEnd?: string
    aEndLocation?: string
    bEnd?: string
    bEndLocation?: string
    bEndDeviceId?: string
    bearer?: string
    bandwidth?: string
    pstn?: string
    transferLimit?: number
    username?: string
    ips?: string[]
    status?: 'online' | 'offline'
    storageQuota?: number
    usedStorageQuota?: number
    vms?: number
    rentalPeriod?: number
    [key: string]: unknown
} | null

export type TNormalizedService = {
    id: string
    name: string
    type: string
    productCode: string
    detailPath: string
    goLiveDate?: string
    orderNumber?: string
    group?: number | string
    groupName?: string
}

export type TServiceDetail = TNormalizedService & {
    resolvedKind: TServiceKind
    billing: TServiceBilling
    additionalDetails: TServiceAdditionalDetails
    graphs: TServiceGraphMeta[]
    raw: unknown
}

export type TServiceNavigationSection = {
    id: string
    label: string
    path: string
    groupId?: number
}
